import React, { useEffect, useState } from "react";
import api from "../../services/api";

export default function LocationsAdmin() {
  const [activeTab, setActiveTab] = useState("regions"); // regions, districts, eglises
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // États pour les données
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [eglises, setEglises] = useState([]);

  // États pour les modales
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // create, edit
  const [editingItem, setEditingItem] = useState(null);

  // États pour les formulaires
  const [formData, setFormData] = useState({
    name: "",
    region_id: "",
    district_id: "",
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    setError("");
    try {
      const [regionsRes, districtsRes, eglisesRes] = await Promise.all([
        api.get("/admin/locations/regions"),
        api.get("/admin/locations/districts"),
        api.get("/admin/locations/eglises"),
      ]);
      setRegions(regionsRes.data || []);
      setDistricts(districtsRes.data || []);
      setEglises(eglisesRes.data || []);
    } catch (err) {
      console.error("Erreur chargement données:", err);
      setError(err?.response?.data?.error || "Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      region_id: "",
      district_id: "",
    });
    setModalType("create");
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      region_id: item.region_id || "",
      district_id: item.district_id || "",
    });
    setModalType("edit");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: "",
      region_id: "",
      district_id: "",
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let url, method, data;

      if (activeTab === "regions") {
        url = modalType === "create" 
          ? "/admin/locations/regions"
          : `/admin/locations/regions/${editingItem.id}`;
        method = modalType === "create" ? "post" : "put";
        data = { name: formData.name.trim() };
      } else if (activeTab === "districts") {
        url = modalType === "create"
          ? "/admin/locations/districts"
          : `/admin/locations/districts/${editingItem.id}`;
        method = modalType === "create" ? "post" : "put";
        data = {
          name: formData.name.trim(),
          region_id: parseInt(formData.region_id),
        };
      } else if (activeTab === "eglises") {
        url = modalType === "create"
          ? "/admin/locations/eglises"
          : `/admin/locations/eglises/${editingItem.id}`;
        method = modalType === "create" ? "post" : "put";
        data = {
          name: formData.name.trim(),
          district_id: parseInt(formData.district_id),
        };
      }

      await api[method](url, data);
      await loadAllData();
      closeModal();
      alert(modalType === "create" ? "Créé avec succès !" : "Mis à jour avec succès !");
    } catch (err) {
      console.error("Erreur soumission:", err);
      setError(err?.response?.data?.error || "Erreur lors de l'opération");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${item.name}" ?`)) {
      return;
    }

    try {
      let url;
      if (activeTab === "regions") {
        url = `/admin/locations/regions/${item.id}`;
      } else if (activeTab === "districts") {
        url = `/admin/locations/districts/${item.id}`;
      } else if (activeTab === "eglises") {
        url = `/admin/locations/eglises/${item.id}`;
      }

      await api.delete(url);
      await loadAllData();
      alert("Supprimé avec succès !");
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert(err?.response?.data?.error || "Erreur lors de la suppression");
    }
  };

  const renderRegionsTable = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      );
    }

    if (regions.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
          <p className="mt-3 text-muted">Aucune région pour le moment.</p>
        </div>
      );
    }

    return (
      <>
        {/* Version desktop : tableau */}
        <div className="table-responsive d-none d-md-block">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Districts</th>
                <th>Églises</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((region) => (
                <tr key={region.id}>
                  <td className="fw-bold">{region.id}</td>
                  <td className="fw-semibold">{region.name}</td>
                  <td>
                    <span className="badge bg-info">{region.district_count || 0}</span>
                  </td>
                  <td>
                    <span className="badge bg-secondary">{region.eglise_count || 0}</span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEditModal(region)}
                    >
                      <i className="bi bi-pencil me-1"></i>Modifier
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(region)}
                    >
                      <i className="bi bi-trash me-1"></i>Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Version mobile : cartes */}
        <div className="d-md-none">
          {regions.map((region) => (
            <div key={region.id} className="card mb-3 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <h6 className="fw-semibold mb-1">{region.name}</h6>
                    <small className="text-muted">ID: {region.id}</small>
                  </div>
                </div>
                
                <div className="mb-2">
                  <small className="text-muted d-block">Districts:</small>
                  <span className="badge bg-info">{region.district_count || 0}</span>
                </div>
                
                <div className="mb-3">
                  <small className="text-muted d-block">Églises:</small>
                  <span className="badge bg-secondary">{region.eglise_count || 0}</span>
                </div>
                
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openEditModal(region)}
                  >
                    <i className="bi bi-pencil me-1"></i>Modifier
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(region)}
                  >
                    <i className="bi bi-trash me-1"></i>Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderDistrictsTable = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      );
    }

    if (districts.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
          <p className="mt-3 text-muted">Aucun district pour le moment.</p>
        </div>
      );
    }

    return (
      <>
        {/* Version desktop : tableau */}
        <div className="table-responsive d-none d-md-block">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Région</th>
                <th>Églises</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {districts.map((district) => (
                <tr key={district.id}>
                  <td className="fw-bold">{district.id}</td>
                  <td className="fw-semibold">{district.name}</td>
                  <td>
                    <span className="badge bg-primary">{district.region_name || "—"}</span>
                  </td>
                  <td>
                    <span className="badge bg-secondary">{district.eglise_count || 0}</span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEditModal(district)}
                    >
                      <i className="bi bi-pencil me-1"></i>Modifier
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(district)}
                    >
                      <i className="bi bi-trash me-1"></i>Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Version mobile : cartes */}
        <div className="d-md-none">
          {districts.map((district) => (
            <div key={district.id} className="card mb-3 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <h6 className="fw-semibold mb-1">{district.name}</h6>
                    <small className="text-muted">ID: {district.id}</small>
                  </div>
                </div>
                
                <div className="mb-2">
                  <small className="text-muted d-block">Région:</small>
                  <span className="badge bg-primary">{district.region_name || "—"}</span>
                </div>
                
                <div className="mb-3">
                  <small className="text-muted d-block">Églises:</small>
                  <span className="badge bg-secondary">{district.eglise_count || 0}</span>
                </div>
                
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openEditModal(district)}
                  >
                    <i className="bi bi-pencil me-1"></i>Modifier
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(district)}
                  >
                    <i className="bi bi-trash me-1"></i>Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderEglisesTable = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      );
    }

    if (eglises.length === 0) {
      return (
        <div className="text-center py-5">
          <i className="bi bi-inbox text-muted" style={{ fontSize: "3rem" }}></i>
          <p className="mt-3 text-muted">Aucune église pour le moment.</p>
        </div>
      );
    }

    return (
      <>
        {/* Version desktop : tableau */}
        <div className="table-responsive d-none d-md-block">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>District</th>
                <th>Région</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {eglises.map((eglise) => (
                <tr key={eglise.id}>
                  <td className="fw-bold">{eglise.id}</td>
                  <td className="fw-semibold">{eglise.name}</td>
                  <td>
                    <span className="badge bg-info">{eglise.district_name || "—"}</span>
                  </td>
                  <td>
                    <span className="badge bg-primary">{eglise.region_name || "—"}</span>
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEditModal(eglise)}
                    >
                      <i className="bi bi-pencil me-1"></i>Modifier
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(eglise)}
                    >
                      <i className="bi bi-trash me-1"></i>Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Version mobile : cartes */}
        <div className="d-md-none">
          {eglises.map((eglise) => (
            <div key={eglise.id} className="card mb-3 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <h6 className="fw-semibold mb-1">{eglise.name}</h6>
                    <small className="text-muted">ID: {eglise.id}</small>
                  </div>
                </div>
                
                <div className="mb-2">
                  <small className="text-muted d-block">District:</small>
                  <span className="badge bg-info">{eglise.district_name || "—"}</span>
                </div>
                
                <div className="mb-3">
                  <small className="text-muted d-block">Région:</small>
                  <span className="badge bg-primary">{eglise.region_name || "—"}</span>
                </div>
                
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => openEditModal(eglise)}
                  >
                    <i className="bi bi-pencil me-1"></i>Modifier
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(eglise)}
                  >
                    <i className="bi bi-trash me-1"></i>Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;

    const title = modalType === "create" 
      ? `Créer une ${activeTab === "regions" ? "région" : activeTab === "districts" ? "district" : "église"}`
      : `Modifier ${activeTab === "regions" ? "la région" : activeTab === "districts" ? "le district" : "l'église"}`;

    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,.5)" }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
              ></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Nom <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder={`Nom de ${activeTab === "regions" ? "la région" : activeTab === "districts" ? "du district" : "l'église"}`}
                  />
                </div>

                {activeTab === "districts" && (
                  <div className="mb-3">
                    <label htmlFor="region_id" className="form-label">
                      Région <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="region_id"
                      value={formData.region_id}
                      onChange={(e) => setFormData({ ...formData, region_id: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner une région</option>
                      {regions.map((region) => (
                        <option key={region.id} value={region.id}>
                          {region.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {activeTab === "eglises" && (
                  <div className="mb-3">
                    <label htmlFor="district_id" className="form-label">
                      District <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="district_id"
                      value={formData.district_id}
                      onChange={(e) => setFormData({ ...formData, district_id: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner un district</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.name} ({district.region_name})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalType === "create" ? "Créer" : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h2 className="text-primary mb-0 mb-2 mb-md-0">Gérer les emplacements</h2>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Onglets */}
      <ul className="nav nav-tabs mb-4 flex-nowrap overflow-auto" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <li className="nav-item flex-shrink-0">
          <button
            className={`nav-link ${activeTab === "regions" ? "active" : ""}`}
            onClick={() => setActiveTab("regions")}
          >
            <i className="bi bi-geo-alt me-2"></i>Régions
          </button>
        </li>
        <li className="nav-item flex-shrink-0">
          <button
            className={`nav-link ${activeTab === "districts" ? "active" : ""}`}
            onClick={() => setActiveTab("districts")}
          >
            <i className="bi bi-geo me-2"></i>Districts
          </button>
        </li>
        <li className="nav-item flex-shrink-0">
          <button
            className={`nav-link ${activeTab === "eglises" ? "active" : ""}`}
            onClick={() => setActiveTab("eglises")}
          >
            <i className="bi bi-building me-2"></i>Églises
          </button>
        </li>
      </ul>

      {/* Contenu des onglets */}
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="mb-0 mb-2 mb-md-0">
            {activeTab === "regions" && <><i className="bi bi-geo-alt me-2"></i>Régions</>}
            {activeTab === "districts" && <><i className="bi bi-geo me-2"></i>Districts</>}
            {activeTab === "eglises" && <><i className="bi bi-building me-2"></i>Églises</>}
          </h5>
          <button className="btn btn-light btn-sm" onClick={openCreateModal}>
            <i className="bi bi-plus-circle me-1"></i>
            <span className="d-none d-sm-inline">Ajouter</span>
            <span className="d-sm-none">+</span>
          </button>
        </div>
        <div className="card-body">
          {activeTab === "regions" && renderRegionsTable()}
          {activeTab === "districts" && renderDistrictsTable()}
          {activeTab === "eglises" && renderEglisesTable()}
        </div>
      </div>

      {/* Modale */}
      {renderModal()}
    </div>
  );
}

